import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { CF_IPFS_GATEWAY_URL } from '@/const'
import WepinBackendAPI from '@/networks/api/wepin/WepinBackendAPI'
import type { Maybe } from '@/types'
import type { INFT } from '@/types/api/nft'
import {
  getExecuteOnce,
  OffsetSettingInfoList,
  arrayPushUnique,
  replaceIPFSUrl,
} from '@/utils'

// V2 NFT Support 타입
interface INFTSupport {
  network: string
  chainId?: number
  isSupported: boolean
}
const useNFTAPIStore = defineStore('nftAPI', () => {
  const nftListByWalletKey = ref<Record<string, Maybe<INFT[]>>>({})
  const nftSupportedList = ref<Maybe<INFTSupport[]>>()
  const _offsetSettingInfoList = new OffsetSettingInfoList<
    typeof fetchNFTList
  >()
  const requestFetchHistory = ref<string[]>([])
  const nftList = computed<Maybe<INFT[]>>(() => {
    // 명시적 루프로 변경하여 Vue 반응성 추적 개선
    const allNfts: INFT[] = []
    for (const [_walletId, nfts] of Object.entries(nftListByWalletKey.value)) {
      if (nfts && Array.isArray(nfts)) {
        allNfts.push(...nfts)
      }
    }
    return allNfts
  })

  const refreshNFTList = getExecuteOnce(
    async (params: { accountIds: string[]; walletId?: string }) => {
      if (!params.accountIds) return
      try {
        const res = await WepinBackendAPI.getInstance().nft.refreshNFTList({
          accountIds: params.accountIds,
          walletId: params.walletId,
        })
        return res.result
      } catch (error) {
        return false
      }
    },
  )

  const fetchNFTList = getExecuteOnce(
    async ({
      walletId,
      force = false,
      needToAddData: _needToAddData = true,
      recursionsParamsData,
    }: {
      walletId: string
      force?: boolean | undefined
      needToAddData?: Boolean
      recursionsParamsData?: number
    }) => {
      if (!walletId) return

      // V2: 페이지네이션은 클라이언트에서 명시적으로 처리
      // 기본적으로 첫 페이지만 조회 (재귀 없음)
      const page = recursionsParamsData ?? 1

      /** API 요청 */
      if (force || !nftListByWalletKey.value[walletId]) {
        const res = await WepinBackendAPI.getInstance().nft.getNFTList({
          walletId: walletId,
          page: page.toString(),
        })
        if (!res) {
          return
        }

        // V2: 첫 페이지(page=1)인 경우 기존 목록 교체, 이후 페이지는 추가
        if (page === 1) {
          // CRITICAL: 전체 객체 재할당으로 Vue 반응성 보장
          nftListByWalletKey.value = {
            ...nftListByWalletKey.value,
            [walletId]: res.nftTokens || [],
          }
        } else {
          const mergedNfts = arrayPushUnique(
            res.nftTokens || [],
            'id',
            nftListByWalletKey.value[walletId] as INFT[],
          )
          // CRITICAL: 전체 객체 재할당으로 Vue 반응성 보장
          nftListByWalletKey.value = {
            ...nftListByWalletKey.value,
            [walletId]: mergedNfts,
          }
        }

        // 외부 메타데이터 비동기 처리
        convertNFTMetaData(nftListByWalletKey.value[walletId] ?? undefined)

        // V2: 페이지네이션 완료 표시 (재귀 호출 안 함)
        if (!requestFetchHistory.value.includes(walletId)) {
          requestFetchHistory.value.push(walletId)
        }
        return nftListByWalletKey.value[walletId]
      }

      // 캐시된 데이터 반환
      return nftListByWalletKey.value[walletId]
    },
  )

  /**
   * 📌 외부 메타데이터를 비동기적으로 가져와 설정하는 함수
   */
  const convertNFTMetaData = async (nftList: INFT[] | undefined) => {
    if (!nftList) return

    await Promise.all(
      nftList.map(async (nft) => {
        if (!nft.addtionalUrl && nft.externalLink) {
          try {
            const response = await fetch(replaceIPFSUrl(nft.externalLink), {
              method: 'GET',
              headers: { 'Content-Type': 'application/json' },
            })

            if (response.ok) {
              const res = await response.json()
              const { name, description, image, animation_url } = res

              if (!nft.name) nft.name = name
              if (!nft.description) nft.description = description
              if (image && image !== '') {
                nft.imageUrl = replaceIPFSUrl(image, CF_IPFS_GATEWAY_URL)
              }
              if (animation_url && animation_url !== '') {
                nft.imageUrl = replaceIPFSUrl(
                  animation_url,
                  CF_IPFS_GATEWAY_URL,
                )
              }
            }
          } catch (error) {
            // console.error('Failed to fetch external metadata:', error)
          }
        }
      }),
    )
  }

  const fetchNFTCount = getExecuteOnce(
    async (
      params: { walletId?: string; accountId?: string },
      force: boolean | undefined = false,
    ) => {
      if (!params) return
      if (force || !nftListByWalletKey.value) {
        const res = await WepinBackendAPI.getInstance().nft.getNFTCount(params)
        if (!res) return
        return res.count
      }
      return { count: nftListByWalletKey.value.length }
    },
  )

  const fetchNFTInfo = getExecuteOnce(
    async (
      walletId: string,
      nftTokenId: string,
      force: boolean | undefined = false,
    ) => {
      if (!nftTokenId) return
      if (force) {
        const res = await WepinBackendAPI.getInstance().nft.getNFTInfo({
          nftTokenId,
        })
        if (!res) return
        const updatedList = nftListByWalletKey.value[walletId]?.map((nft) =>
          nft.id === res.nftToken.id ? res.nftToken : nft,
        )
        // CRITICAL: 전체 객체 재할당으로 Vue 반응성 보장
        nftListByWalletKey.value = {
          ...nftListByWalletKey.value,
          [walletId]: updatedList,
        }
        return res.nftToken
      }
      return nftListByWalletKey.value[walletId]?.find(
        (nft) => nft.id.toString() === nftTokenId,
      )
    },
  )

  const fetchSupportedNFTList = getExecuteOnce(
    async (force: boolean | undefined = false) => {
      if (force || !nftSupportedList.value) {
        const res =
          await WepinBackendAPI.getInstance().nft.getSupportedNFTList()
        if (!res) return
        if (!nftSupportedList.value) nftSupportedList.value = []
        nftSupportedList.value = nftSupportedList.value.concat(
          res.supportNetworkList,
        )
      }
      return nftSupportedList.value
    },
  )

  const deleteNFT = (walletId: string, nftTokenId: string) => {
    const filteredList = nftListByWalletKey.value[walletId]?.filter(
      (nft) => nft.id.toString() !== nftTokenId,
    )
    // CRITICAL: 전체 객체 재할당으로 Vue 반응성 보장
    nftListByWalletKey.value = {
      ...nftListByWalletKey.value,
      [walletId]: filteredList,
    }
  }

  const reset = () => {
    nftListByWalletKey.value = {}
    nftSupportedList.value = undefined
    requestFetchHistory.value = []
  }
  return {
    nftSupportedList,
    nftListByWalletKey,
    refreshNFTList,
    fetchNFTList,
    fetchNFTCount,
    fetchNFTInfo,
    fetchSupportedNFTList,
    nftList,
    requestFetchHistory,
    deleteNFT,
    reset,
  }
})

export { useNFTAPIStore }
